import React, { useEffect, useState } from 'react'
import axios from 'axios'
import { toast } from 'react-hot-toast'
import { Boxes, Plus, Wifi, Trash2, Loader2, CheckCircle2, XCircle } from 'lucide-react'
import { getSession } from '../lib/session'

// Optional: register your OWN MCP servers so the Tool-using Agent can call their
// tools during a run. Off by default — add none and the agent uses only its
// built-in tools. Tokens are encrypted; your call whether to connect any.
export default function McpServers() {
  const ns = getSession()?.namespaceId
  const [servers, setServers] = useState([])
  const [form, setForm] = useState({ name: '', url: '', token: '', enabled: true })
  const [busy, setBusy] = useState(false)
  const [tested, setTested] = useState({}) // name -> {ok, tools|detail}

  const load = () => { if (ns) axios.get(`/api/v1/tenants/${ns}/mcp-servers`).then(r => setServers(r.data.servers || [])).catch(() => {}) }
  useEffect(load, [ns])

  const save = async () => {
    if (!form.name.trim() || !form.url.trim()) { toast.error('Name and URL are required'); return }
    setBusy(true)
    try {
      await axios.post(`/api/v1/tenants/${ns}/mcp-servers`, { name: form.name.trim(), url: form.url.trim(), token: form.token || undefined, enabled: form.enabled })
      toast.success('Saved'); setForm({ name: '', url: '', token: '', enabled: true }); load()
    } catch (err) { toast.error(err.response?.data?.error || 'Save failed') } finally { setBusy(false) }
  }
  const toggle = async (s) => { await axios.post(`/api/v1/tenants/${ns}/mcp-servers/${s.name}/enabled`, { enabled: !s.enabled }).catch(() => {}); load() }
  const test = async (s) => {
    setTested(t => ({ ...t, [s.name]: { loading: true } }))
    try { const { data } = await axios.post(`/api/v1/tenants/${ns}/mcp-servers/${s.name}/test`); setTested(t => ({ ...t, [s.name]: data })) }
    catch { setTested(t => ({ ...t, [s.name]: { ok: false, detail: 'failed' } })) }
  }
  const remove = async (s) => { if (!confirm(`Remove MCP server ${s.name}?`)) return; await axios.delete(`/api/v1/tenants/${ns}/mcp-servers/${s.name}`).catch(() => {}); load() }

  return (
    <div className="card">
      <div className="flex items-center gap-2 mb-1"><Boxes size={20} className="text-primary-600" /><h3 className="text-lg font-semibold text-gray-900">Your MCP servers</h3></div>
      <p className="text-sm text-gray-500 mb-4">Optional. Connect your own MCP servers and the Tool-using Agent can call their tools during a run — alongside its built-in ones. Add none and nothing changes. Tokens are encrypted at rest.</p>

      <div className="rounded-xl border border-gray-200 p-4 space-y-3 bg-gray-50/60">
        <div className="grid sm:grid-cols-2 gap-3">
          <input className="input" placeholder="Name (e.g. github)" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} />
          <input className="input" placeholder="MCP URL — https://…/mcp" value={form.url} onChange={e => setForm(f => ({ ...f, url: e.target.value }))} />
        </div>
        <input className="input" type="password" placeholder="Auth token (optional — sent as Authorization: Bearer …)" value={form.token} onChange={e => setForm(f => ({ ...f, token: e.target.value }))} />
        <div className="flex items-center gap-3">
          <label className="flex items-center gap-2 text-sm text-gray-700"><input type="checkbox" checked={form.enabled} onChange={e => setForm(f => ({ ...f, enabled: e.target.checked }))} /> Enabled</label>
          <button onClick={save} disabled={busy} className="btn-primary flex items-center gap-2 disabled:opacity-60">{busy ? <Loader2 className="animate-spin" size={15} /> : <Plus size={15} />} Add server</button>
        </div>
      </div>

      <div className="mt-4 divide-y divide-gray-100">
        {servers.length === 0 ? <p className="text-sm text-gray-400">No MCP servers connected.</p> : servers.map(s => (
          <div key={s.name} className="py-3">
            <div className="flex items-center gap-3">
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-gray-900">{s.name} {!s.enabled && <span className="text-xs text-gray-400">(disabled)</span>}</p>
                <p className="text-xs text-gray-500 font-mono truncate">{s.url}</p>
              </div>
              <label className="flex items-center gap-1.5 text-xs text-gray-600"><input type="checkbox" checked={s.enabled} onChange={() => toggle(s)} /> on</label>
              <button onClick={() => test(s)} className="p-1.5 text-gray-400 hover:text-gray-800" title="Test"><Wifi size={15} /></button>
              <button onClick={() => remove(s)} className="p-1.5 text-gray-400 hover:text-red-500" title="Remove"><Trash2 size={15} /></button>
            </div>
            {tested[s.name] && !tested[s.name].loading && (
              <div className={`mt-1.5 text-xs flex items-center gap-1.5 ${tested[s.name].ok ? 'text-green-700' : 'text-red-700'}`}>
                {tested[s.name].ok ? <CheckCircle2 size={13} /> : <XCircle size={13} />}
                {tested[s.name].ok ? `${tested[s.name].tools.length} tool(s): ${tested[s.name].tools.join(', ') || '—'}` : `Failed — ${tested[s.name].detail || ''}`}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  )
}
